import { useMutation } from "@apollo/client";

import { FormGroup } from "@cofano/cck";

import { Language } from "imports/Utils";

import ContractStatusSelect from "components/contract/ContractStatusSelect";

import {
	ContractStatusChangerContractFragment,
} from "__generated__/view/contractsNew/graphql/contract.fragments.generated";
import ApplicationDefinedStatusChanger, {
	ApplicationDefinedDialogProps,
} from "view/statuses/components/changer/ApplicationDefinedStatusChanger";
import { FormDialog } from "components/dialogs/Dialogs";
import { SetContractStatusChangerDocument } from "__generated__/view/contractsNew/graphql/contract.mutations.generated";
import { Controller, useForm } from "react-hook-form";
import {
	ContractStatusChangerInput,
	ContractStatusChangerOutput,
	ContractStatusChangerSchema,
} from "view/contractsNew/validation/contract.zod";
import { zodResolver } from "@hookform/resolvers/zod";
import FormField from "components/cck/FormField";
import { ContractStatus } from "__generated__/enums";

type ContractStatusChangerProps = {
	contract: ContractStatusChangerContractFragment
	refetch: () => void;
	disabled?: boolean;
};

const ContractStatusChanger = ({
	contract,
	refetch,
	disabled,
}: ContractStatusChangerProps) => (
	<ApplicationDefinedStatusChanger
		currentStatus={contract.status}
		disabled={disabled || contract.status.name === ContractStatus.Inactive}
		renderDialog={(dialogProps) => (
			<ContractStatusChangerDialog
				contract={contract}
				dialogProps={dialogProps}
				refetch={refetch}
			/>
		)}
	/>
);

const ContractStatusChangerDialog = ({
	contract,
	refetch,
	dialogProps,
}: ApplicationDefinedDialogProps<ContractStatusChangerProps>) => {

	const [ changeContractStatus, { loading } ] = useMutation(SetContractStatusChangerDocument);

	const { control, handleSubmit, reset, formState: { errors } } = useForm<ContractStatusChangerInput, unknown, ContractStatusChangerOutput>({
		resolver: zodResolver(ContractStatusChangerSchema),
		defaultValues: {
			selectedStatus: contract.status,
		} satisfies ContractStatusChangerInput,
	});

	const onStatusChange = async (data: ContractStatusChangerOutput) => {
		await changeContractStatus({
			variables: {
				contractId: contract.id,
				status: data.selectedStatus.name,
			},
		});

		refetch();
		dialogProps.onClose();
	};

	return (
		<FormDialog
			{...dialogProps}
			maxWidth="xs"
			onClose={() => {
				dialogProps.onClose();
				reset();
			}}
			loading={loading}
			form="contract-status-changer-form"
		>
			<form id="contract-status-changer-form" onSubmit={handleSubmit(onStatusChange)}>
				<FormGroup label={Language("STATUS")}>
					<FormField name="selectedStatus" errors={errors}>
						<Controller
							name="selectedStatus"
							control={control}
							render={({ field }) => (
								<ContractStatusSelect
									value={field.value}
									currentStatus={contract.status.name}
									onChange={field.onChange}
									clearable={false}
								/>
							)}
						/>
					</FormField>
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default ContractStatusChanger;
